import { useMemo } from 'react';

import { DatePicker } from '@atlaskit/datetime-picker';

type DateFilterName = 'executedAfter' | 'executedBefore';

type DateFilterProps = {
  name: DateFilterName;
  placeholder: string;
  filterValue?: string;
  onFilterChange: (date?: string) => void;
};

const SIX_MONTHS_IN_DAYS = 180;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const createDateBoundaries = () => {
  const now = new Date();
  const sixMonthsAgo = new Date(
    now.getTime() - SIX_MONTHS_IN_DAYS * MILLISECONDS_PER_DAY
  );

  return {
    today: now,
    sixMonthsAgo,
  };
};

const convertToIsoString = (dateString?: string): string | undefined => {
  return dateString ? new Date(dateString).toISOString() : undefined;
};

const handleDateConversion = (date: string): string | undefined => {
  try {
    return new Date(date).toISOString();
  } catch (error) {
    throw new Error(`Failed to convert date to ISO string: ${error}`);
  }
};

export const DateFilter = ({
  name,
  placeholder,
  filterValue,
  onFilterChange,
}: DateFilterProps) => {
  const { today, sixMonthsAgo } = useMemo(createDateBoundaries, []);

  const dateValue = useMemo(
    () => convertToIsoString(filterValue),
    [filterValue]
  );

  const handleDateChange = (selectedDate: string | null) => {
    if (!selectedDate) {
      onFilterChange(undefined);
      return;
    }

    const isoString = handleDateConversion(selectedDate);
    onFilterChange(isoString);
  };

  return (
    <DatePicker
      appearance="subtle"
      clearControlLabel={`Clear ${name} date`}
      id={`date-picker-${name}`}
      inputLabelId={`date-${name}`}
      maxDate={today.toISOString()}
      minDate={sixMonthsAgo.toISOString()}
      onChange={handleDateChange}
      openCalendarLabel="Open calendar"
      placeholder={placeholder}
      shouldShowCalendarButton
      value={dateValue}
    />
  );
};
